/**
 * 人員編輯表單組件 | Person Edit Form Component
 * 用於新增或編輯人員資料 | Used for creating or editing person data
 */

import { useState, useEffect, useCallback } from 'react';
import { Person, PersonEdit } from '@/types/person';
import { PersonImpact } from '@/types/person';
import { useResourceStore } from '@/store/resourceStore';
import { useOrderStore } from '@/store';
import { analyzePersonImpact, formatImpactSummary } from '@/utils/personImpactAnalyzer';
import { toast } from '@/components/UI/Toast';
import { ConfirmDialog } from '@/components/UI/Modal';

interface PersonFormProps {
  person?: Person;              // 要編輯的人員（不提供則為新增）| Person to edit (undefined = create new)
  onClose: () => void;          // 關閉表單 | Close form
  onSave: (person: Person) => void;  // 保存回調 | Save callback
}

// 可用廠別選項 | Available plant options
const PLANT_OPTIONS = ['台北廠', '桃園廠', '新竹廠', '台中廠', '台南廠', '高雄廠'];

// 可用部門選項 | Available department options
const DEPARTMENT_OPTIONS = [
  '生產部',
  '品質部',
  '工程部',
  '研發部',
  '維護部',
  '物流部',
  '人事部',
];

// 可用角色選項 | Available role options
const ROLE_OPTIONS = [
  '操作員',
  '技術員',
  '工程師',
  '主管',
  '品檢員',
  '維護人員',
  '物流人員',
];

// 可用技能選項 | Available skill options
const SKILL_OPTIONS = [
  '機械操作',
  '品質檢驗',
  '設備維護',
  '程式設計',
  '焊接',
  '組裝',
  '測試',
  '包裝',
];

export function PersonForm({ person, onClose, onSave }: PersonFormProps) {
  const { updatePerson, createPerson } = useResourceStore();
  const { orders, mainWorkOrders, subWorkOrders } = useOrderStore();

  // 表單狀態 | Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PersonEdit>(() => {
    if (person) {
      return { ...person };
    }
    // 預設新人員值 | Default new person values
    return {
      id: '',
      name: '',
      email: '',
      plants: [],
      department: '',
      roles: [],
      shiftType: '常日班',
      status: '啟用',
      dailyHours: 8,
      skills: [],
      hireDate: new Date().toISOString().split('T')[0],
    };
  });

  // 影響分析結果 | Impact analysis result
  const [impact, setImpact] = useState<PersonImpact | null>(null);
  const [showImpact, setShowImpact] = useState(false);

  // 變更原因 | Change reason
  const [reason, setReason] = useState('');

  // 取消確認對話框 | Cancel confirmation dialog
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // 驗證錯誤 | Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  // 追蹤已被觸碰的欄位 | Track touched fields
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // 即時驗證單一欄位 | Real-time validate single field
  const validateField = useCallback((field: keyof PersonEdit, value: any): string => {
    switch (field) {
      case 'id':
        if (!value.trim()) return '工號不能為空';
        if (value.trim().length < 2) return '工號至少需要 2 個字元';
        return '';
      case 'name':
        if (!value.trim()) return '姓名不能為空';
        if (value.trim().length < 2) return '姓名至少需要 2 個字元';
        return '';
      case 'email':
        if (!value.trim()) return '電子郵件不能為空';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return '電子郵件格式不正確';
        return '';
      case 'plants':
        if ((value as string[]).length === 0) return '至少選擇一個廠別';
        return '';
      case 'department':
        if (!value.trim()) return '部門不能為空';
        return '';
      case 'roles':
        if ((value as string[]).length === 0) return '至少選擇一個角色';
        return '';
      case 'skills':
        if ((value as string[]).length === 0) return '至少選擇一個技能';
        return '';
      case 'hireDate':
        if (!value) return '僱用日期不能為空';
        return '';
      default:
        return '';
    }
  }, []);

  // 處理欄位變更 | Handle field change
  const handleFieldChange = useCallback((field: keyof PersonEdit, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // 如果該欄位已被觸碰，進行即時驗證 | Real-time validate if touched
    if (touched.has(field)) {
      const error = validateField(field, value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[field] = error;
        } else {
          delete newErrors[field];
        }
        return newErrors;
      });
    } else {
      // 如果未被觸碰，只清除錯誤 | Just clear error if not touched
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [touched, validateField]);

  // 處理欄位失焦 | Handle field blur
  const handleFieldBlur = useCallback((field: keyof PersonEdit) => {
    setTouched((prev) => new Set([...prev, field]));
    const error = validateField(field, formData[field]);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  }, [formData, validateField]);

  // 處理陣列欄位的切換（plants, roles, skills）| Handle array field toggle
  const handleArrayToggle = useCallback((field: 'plants' | 'roles' | 'skills', value: string) => {
    const currentArray = formData[field] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter((item) => item !== value)
      : [...currentArray, value];

    // 對陣列欄位進行即時驗證（因為 checkbox 點擊是即時交互）| Real-time validate array fields (checkbox click is immediate)
    setFormData((prev) => ({ ...prev, [field]: newArray }));
    setTouched((prev) => new Set([...prev, field]));

    const error = validateField(field, newArray);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  }, [formData, validateField]);

  // 驗證表單 | Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) newErrors.id = '工號不能為空';
    if (!formData.name.trim()) newErrors.name = '姓名不能為空';
    if (!formData.email.trim()) newErrors.email = '電子郵件不能為空';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '電子郵件格式不正確';
    }
    if (formData.plants.length === 0) newErrors.plants = '至少選擇一個廠別';
    if (!formData.department.trim()) newErrors.department = '部門不能為空';
    if (formData.roles.length === 0) newErrors.roles = '至少選擇一個角色';
    if (formData.skills.length === 0) newErrors.skills = '至少選擇一個技能';
    if (!formData.hireDate) newErrors.hireDate = '僱用日期不能為空';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 分析影響 | Analyze impact
  const analyzeImpact = useCallback(() => {
    if (!person || person.id === formData.id) {
      setImpact(null);
      return;
    }

    const result = analyzePersonImpact(
      { oldPerson: person, newPerson: formData },
      orders,
      mainWorkOrders,
      subWorkOrders
    );

    setImpact(result);
    setShowImpact(true);
  }, [person, formData, orders, mainWorkOrders, subWorkOrders]);

  // 當 ID 變更時自動分析影響 | Auto-analyze impact when ID changes
  useEffect(() => {
    if (person && person.id !== formData.id) {
      analyzeImpact();
    } else {
      setImpact(null);
      setShowImpact(false);
    }
  }, [formData.id, person, analyzeImpact]);

  // 提交表單 | Submit form
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // 防止重複提交 | Prevent duplicate submission
      if (isSubmitting) return;

      // 表單驗證
      if (!formData.id.trim()) {
        toast.error('請輸入人員 ID', '人員 ID 為必填項目');
        return;
      }
      if (!formData.name.trim()) {
        toast.error('請輸入姓名', '姓名為必填項目');
        return;
      }
      if (!formData.email.trim()) {
        toast.error('請輸入電子郵件', '電子郵件為必填項目');
        return;
      }
      if (!formData.department) {
        toast.error('請選擇部門', '部門為必填項目');
        return;
      }
      if (formData.roles.length === 0) {
        toast.error('請選擇至少一個角色', '角色為必填項目');
        return;
      }
      if (formData.plants.length === 0) {
        toast.error('請選擇至少一個廠別', '廠別為必填項目');
        return;
      }

      if (!validateForm()) {
        toast.error('表單驗證失敗', '請檢查表單內容是否正確');
        return;
      }

      // 檢查是否有 ID 變更且未確認 | Check if ID changed but not confirmed
      if (person && person.id !== formData.id && !formData.confirmChanges) {
        toast.error('請確認 ID 變更影響', 'ID 變更會影響相關工單，請先確認');
        return;
      }

      setIsSubmitting(true);

      try {
        if (person) {
          // 編輯現有人員 | Edit existing person
          updatePerson(person.id, formData, reason);
          toast.success(
            '人員資料已更新',
            `${formData.name} 的資料已成功更新`,
            3000
          );
        } else {
          // 建立新人員 | Create new person
          createPerson(formData);
          toast.success(
            '新人員已建立',
            `${formData.name} (${formData.id}) 已成功建立`,
            3000
          );
        }

        onSave(formData);
      } catch (error) {
        console.error('保存人員資料時發生錯誤:', error);
        toast.error(
          '保存失敗',
          error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
          5000
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, person, validateForm, updatePerson, createPerson, reason, onSave, isSubmitting]
  );

  // 取消按鈕處理 | Cancel button handler
  const handleCancel = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  // 確認取消 | Confirm cancel
  const confirmCancel = useCallback(() => {
    setShowCancelConfirm(false);
    onClose();
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-[#e2e8f0] rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* 標題列 | Header */}
        <div className="sticky top-0 bg-[#f8fafc] border-b border-[#e2e8f0] px-6 py-4">
          <h2 className="text-xl font-semibold text-[#0f172a]">
            {person ? '✏️ 編輯人員資料' : '➕ 新增人員'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* 影響分析警告 | Impact analysis warning */}
          {showImpact && impact && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-600 mb-2">
                    ID 變更影響警告
                  </h3>
                  <p className="text-sm text-[#475569] mb-3">
                    {formatImpactSummary(impact)}
                  </p>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-[#0066cc] hover:text-[#0052a3] mb-2">
                      查看完整影響報告
                    </summary>
                    <div className="mt-2 pl-4 border-l-2 border-red-500 space-y-2">
                      <div>
                        <strong>受影響訂單 ({impact.totalOrderCount})：</strong>
                        <ul className="list-disc list-inside ml-2">
                          {impact.affectedOrders.map((order) => (
                            <li key={order.orderId}>
                              {order.orderProductName} ({order.orderCustomer})
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong>受影響工單 ({impact.totalMainWorkOrderCount} 個主工單, {impact.totalSubWorkOrderCount} 個子工單)：</strong>
                        <ul className="list-disc list-inside ml-2">
                          {impact.affectedWorkOrders.map((mwo) => (
                            <li key={mwo.mainWorkOrderId}>
                              {mwo.mainWorkOrderName}
                              <ul className="list-disc list-inside ml-4">
                                {mwo.subWorkOrders.map((swo) => (
                                  <li key={swo.subWorkOrderId}>
                                    {swo.processName} ({swo.processCode})
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
              <label className="mt-3 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.confirmChanges || false}
                  onChange={(e) => handleFieldChange('confirmChanges', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-[#f8fafc]">
                  我已了解並確認上述影響，繼續進行變更
                </span>
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 基本資訊 | Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0f172a] border-b border-[#e2e8f0] pb-2">
                📋 基本資訊
              </h3>

              {/* 工號 | Employee ID */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  工號 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => handleFieldChange('id', e.target.value)}
                  onBlur={() => handleFieldBlur('id')}
                  className={`w-full px-3 py-2 bg-white border transition-colors ${
                    errors.id ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-[#cbd5e1]'
                  } rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]`}
                  placeholder="例如：EMP001"
                />
                {errors.id && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.id}</span></p>}
              </div>

              {/* 姓名 | Name */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  className={`w-full px-3 py-2 bg-white border transition-colors ${
                    errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-[#cbd5e1]'
                  } rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]`}
                  placeholder="例如：張三"
                />
                {errors.name && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.name}</span></p>}
              </div>

              {/* 電子郵件 | Email */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  電子郵件 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  onBlur={() => handleFieldBlur('email')}
                  className={`w-full px-3 py-2 bg-white border transition-colors ${
                    errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-[#cbd5e1]'
                  } rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]`}
                  placeholder="例如：user@example.com"
                />
                {errors.email && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.email}</span></p>}
              </div>

              {/* 部門 | Department */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  部門 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleFieldChange('department', e.target.value)}
                  onBlur={() => handleFieldBlur('department')}
                  className={`w-full px-3 py-2 bg-white border transition-all duration-200 ${
                    errors.department ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-[#cbd5e1]'
                  } rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc] focus:ring-2 focus:ring-[#0066cc]/20`}
                >
                  <option value="">請選擇部門</option>
                  {DEPARTMENT_OPTIONS.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
                {errors.department && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.department}</span></p>}
              </div>

              {/* 僱用日期 | Hire Date */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  僱用日期 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => handleFieldChange('hireDate', e.target.value)}
                  onBlur={() => handleFieldBlur('hireDate')}
                  className={`w-full px-3 py-2 bg-white border transition-colors ${
                    errors.hireDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-[#cbd5e1]'
                  } rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]`}
                />
                {errors.hireDate && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.hireDate}</span></p>}
              </div>
            </div>

            {/* 工作資訊 | Work Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0f172a] border-b border-[#e2e8f0] pb-2">
                💼 工作資訊
              </h3>

              {/* 廠別 | Plants */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  廠別 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-[#f8fafc] border border-[#e2e8f0] rounded">
                  {PLANT_OPTIONS.map((plant) => (
                    <label key={plant} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.plants.includes(plant)}
                        onChange={() => handleArrayToggle('plants', plant)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-[#0f172a]">{plant}</span>
                    </label>
                  ))}
                </div>
                {errors.plants && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.plants}</span></p>}
              </div>

              {/* 角色 | Roles */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  角色 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-[#f8fafc] border border-[#e2e8f0] rounded">
                  {ROLE_OPTIONS.map((role) => (
                    <label key={role} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role)}
                        onChange={() => handleArrayToggle('roles', role)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-[#0f172a]">{role}</span>
                    </label>
                  ))}
                </div>
                {errors.roles && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.roles}</span></p>}
              </div>

              {/* 技能 | Skills */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  技能 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-[#f8fafc] border border-[#e2e8f0] rounded">
                  {SKILL_OPTIONS.map((skill) => (
                    <label key={skill} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.skills.includes(skill)}
                        onChange={() => handleArrayToggle('skills', skill)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-[#0f172a]">{skill}</span>
                    </label>
                  ))}
                </div>
                {errors.skills && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert"><span aria-hidden="true">⚠️</span><span>{errors.skills}</span></p>}
              </div>

              {/* 班別 | Shift Type */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  班別 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="shiftType"
                      value="常日班"
                      checked={formData.shiftType === '常日班'}
                      onChange={(e) => handleFieldChange('shiftType', e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-[#0f172a]">常日班</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="shiftType"
                      value="輪班制"
                      checked={formData.shiftType === '輪班制'}
                      onChange={(e) => handleFieldChange('shiftType', e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-[#0f172a]">輪班制</span>
                  </label>
                </div>
              </div>

              {/* 每日工時 | Daily Hours */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  每日工時 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={formData.dailyHours}
                  onChange={(e) => handleFieldChange('dailyHours', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc]"
                />
              </div>

              {/* 狀態 | Status */}
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  狀態 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc] transition-all duration-200 focus:ring-2 focus:ring-[#0066cc]/20"
                >
                  <option value="啟用">啟用</option>
                  <option value="停用">停用</option>
                </select>
              </div>
            </div>
          </div>

          {/* 變更原因 | Change Reason */}
          {person && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-[#475569] mb-1">
                變更原因（選填）
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#cbd5e1] rounded text-[#0f172a] focus:outline-none focus:border-[#0066cc] transition-all duration-200 focus:ring-2 focus:ring-[#0066cc]/20"
                rows={2}
                placeholder="請說明變更原因..."
              />
            </div>
          )}

          {/* 操作按鈕 | Action Buttons */}
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-[#e2e8f0]">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-[#e2e8f0] hover:bg-[#cbd5e1] text-[#0f172a] rounded transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#0066cc] hover:bg-[#0052a3] text-white rounded transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  <span>處理中...</span>
                </>
              ) : (
                <>{person ? '💾 儲存變更' : '➕ 新增人員'}</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 取消確認對話框 | Cancel confirmation dialog */}
      {showCancelConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="確認取消"
          message="確定要取消嗎？所有未保存的變更將會丟失。"
          confirmLabel="確認取消"
          cancelLabel="繼續編輯"
          type="warning"
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={confirmCancel}
        />
      )}
    </div>
  );
}
